/**
 * Scope selection, persisted in the URL.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The URL rather than component state so the view survives a reload and can be
 * pasted to a colleague ("here's what France sees"). Two readable params rather
 * than one packed token, so the address bar stays self-explanatory.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_SCOPE,
  scopeCampaignKey,
  scopeFromParams,
  type TagRuleScope,
} from './model';
import { usePinnedTagRuleScope } from './scope-pin';

const COUNTRY = 'country';
const CAMPAIGN = 'campaign';

/** Pinned scopes are not selectable, so the setter is inert rather than absent
 *  — every consumer can keep calling it unconditionally. */
const NOOP_SET_SCOPE = (): void => undefined;

export function useTagRuleScope(): {
  scope: TagRuleScope;
  setScope: (next: TagRuleScope) => void;
} {
  const pinned = usePinnedTagRuleScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const country = searchParams.get(COUNTRY);
  const campaign = searchParams.get(CAMPAIGN);

  const scope = useMemo(() => scopeFromParams(country, campaign), [country, campaign]);

  const setScope = useCallback(
    (next: TagRuleScope) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Absence IS the default, so a link without these params always lands
          // on the familiar view.
          const c = next.countryKey?.trim();
          if (c) params.set(COUNTRY, c);
          else params.delete(COUNTRY);
          const camp = scopeCampaignKey(next);
          if (c && camp) params.set(CAMPAIGN, camp);
          else params.delete(CAMPAIGN);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // A pinned scope wins: the caller already knows whose rules these are (the
  // FO drawer opens on ONE campaign), so there is nothing to choose and nothing
  // to write to the URL — the drawer does not own the address bar.
  if (pinned) return { scope: pinned, setScope: NOOP_SET_SCOPE };
  return { scope, setScope };
}

export { DEFAULT_SCOPE };
